/**
 * 时间计算工具函数
 * 用于计算下注周期的倒计时和下一个时间点
 */

/**
 * 计算下一个周期时间点的剩余时间
 * @param {number} periodMinutes - 周期分钟数 (5, 15, 30)
 * @returns {Object} - { remainingMinutes, remainingSeconds, totalSeconds }
 */
export function getNextPeriodCountdown(periodMinutes) {
  const now = new Date();
  const currentMinutes = now.getMinutes();
  const currentSeconds = now.getSeconds();
  
  // 计算当前时间在周期内的位置
  let nextPeriodMinute;
  if (periodMinutes === 5) {
    // 5分钟周期: 0, 5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55
    nextPeriodMinute = Math.ceil(currentMinutes / 5) * 5;
    if (nextPeriodMinute === currentMinutes && currentSeconds === 0) {
      nextPeriodMinute += 5;
    }
  } else if (periodMinutes === 15) {
    // 15分钟周期: 0, 15, 30, 45
    nextPeriodMinute = Math.ceil(currentMinutes / 15) * 15;
    if (nextPeriodMinute === currentMinutes && currentSeconds === 0) {
      nextPeriodMinute += 15;
    }
  } else if (periodMinutes === 30) {
    // 30分钟周期: 0, 30
    nextPeriodMinute = Math.ceil(currentMinutes / 30) * 30;
    if (nextPeriodMinute === currentMinutes && currentSeconds === 0) {
      nextPeriodMinute += 30;
    }
  }
  
  // 处理跨小时情况
  if (nextPeriodMinute >= 60) {
    nextPeriodMinute = nextPeriodMinute - 60;
  }
  
  // 计算到下一个周期点的总秒数
  let totalSecondsRemaining;
  if (nextPeriodMinute > currentMinutes) {
    // 同一小时内
    totalSecondsRemaining = (nextPeriodMinute - currentMinutes) * 60 - currentSeconds;
  } else {
    // 跨小时
    totalSecondsRemaining = (60 - currentMinutes + nextPeriodMinute) * 60 - currentSeconds;
  }
  
  // 如果计算结果为0或负数，说明刚好到了周期点，跳到下一个周期
  if (totalSecondsRemaining <= 0) {
    totalSecondsRemaining = periodMinutes * 60;
  }
  
  // 转换为分钟和秒数
  const remainingMinutes = Math.floor(totalSecondsRemaining / 60);
  const remainingSeconds = totalSecondsRemaining % 60;
  
  return {
    remainingMinutes,
    remainingSeconds,
    totalSeconds: totalSecondsRemaining
  };
}

/**
 * 格式化倒计时显示
 * @param {number} minutes - 剩余分钟
 * @param {number} seconds - 剩余秒数
 * @returns {string} - 格式化的时间字符串 "MM:SS"
 */
export function formatCountdown(minutes, seconds) {
  // 确保分钟和秒数都是正数
  const validMinutes = Math.max(0, Math.floor(minutes));
  const validSeconds = Math.max(0, Math.floor(seconds));
  
  // 格式化为 MM:SS 格式
  const mm = validMinutes.toString().padStart(2, '0');
  const ss = validSeconds.toString().padStart(2, '0');
  return `${mm}:${ss}`;
}

/**
 * 获取下一个具体的周期时间点
 * @param {number} periodMinutes - 周期分钟数
 * @returns {string} - 下一个周期时间点 "HH:MM"
 */
export function getNextPeriodTime(periodMinutes) {
  const now = new Date();
  const currentMinutes = now.getMinutes();
  const currentHours = now.getHours();
  
  // 计算下一个周期点
  const nextPeriodMinutes = Math.ceil((currentMinutes + 1) / periodMinutes) * periodMinutes;
  
  let nextHours = currentHours;
  let nextMinutes = nextPeriodMinutes;
  
  // 处理跨小时的情况
  if (nextMinutes >= 60) {
    nextHours = (currentHours + Math.floor(nextMinutes / 60)) % 24;
    nextMinutes = nextMinutes % 60;
  }
  
  const hh = nextHours.toString().padStart(2, '0');
  const mm = nextMinutes.toString().padStart(2, '0');
  
  return `${hh}:${mm}`;
}

/**
 * 计算周期的总秒数
 * @param {number} periodMinutes - 周期分钟数
 * @returns {number} - 总秒数
 */
export function getPeriodTotalSeconds(periodMinutes) {
  return periodMinutes * 60;
}
